import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS } from "./adminStyles";

const API_URL = getBaseURL();

const ManagePlayerTemplatesInner = () => {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ 
    title: "", 
    entry_fee: "", 
    prize_pool: "", 
    total_spots: "",
    prize_distribution: [] 
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else fetchTemplates();
  }, [token, navigate]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/player-challenge-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(res.data);
    } catch (err) {
      toast.error("Failed to load player templates.");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    if (!form.title.trim()) { toast.warn("Template title is required."); return false; }
    if (!form.entry_fee || isNaN(form.entry_fee)) { toast.warn("Valid entry fee is required."); return false; }
    if (!form.prize_pool || isNaN(form.prize_pool)) { toast.warn("Prize pool is required."); return false; }
    if (!form.total_spots || isNaN(form.total_spots)) { toast.warn("Total spots is required."); return false; }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        entry_fee: Number(form.entry_fee),
        prize_pool: Number(form.prize_pool),
        total_spots: Number(form.total_spots),
        prize_distribution: form.prize_distribution
      };
      if (editingId) {
        await axios.put(`${API_URL}/admin/player-challenge-templates/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Player template updated!");
      } else {
        await axios.post(`${API_URL}/admin/player-challenge-templates`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Player template created!");
      }
      setForm({ title: "", entry_fee: "", prize_pool: "", total_spots: "", prize_distribution: [] });
      setEditingId(null);
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete tier "${title}"? This affects all player challenge categories.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/player-challenge-templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Template deleted.");
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to delete template.");
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({ 
      title: t.title, 
      entry_fee: t.entry_fee, 
      prize_pool: t.prize_pool, 
      total_spots: t.total_spots,
      prize_distribution: Array.isArray(t.prize_distribution) ? t.prize_distribution : []
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>🎴 Master Player Tiers</div>
          <div style={S.pageSubtitle}>Automatic contest tiers for player challenges</div>
        </div>
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "12px", padding: "12px 18px", textAlign: "center" }}>
          <div style={{ fontSize: "22px", fontWeight: 800, color: COLORS.indigo }}>{templates.length}</div>
          <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Master Tiers</div>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.cardTitle}>{editingId ? "✏️ Edit Master Tier" : "➕ Create Master Tier"}</div>
        <div style={{ fontSize: "12px", color: COLORS.textMuted, marginBottom: "20px" }}>
          These tiers are automatically applied to all 8 categories (Sixes, Combo, MotM, etc.) for every match.
        </div>
        <form onSubmit={handleSave}>
          <div style={{ marginBottom: "16px" }}>
            <label style={S.label}>Tier Title *</label>
            <input
              className="admin-input-focus"
              style={S.input}
              placeholder="e.g. Mega Player Challenge"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={S.label}>Entry Fee (₹)</label>
              <input
                className="admin-input-focus"
                type="number"
                min="0"
                style={S.input}
                value={form.entry_fee}
                onChange={e => setForm({ ...form, entry_fee: e.target.value })}
                required
              />
            </div>
            <div>
              <label style={S.label}>Total Prize Pool (₹)</label>
              <input
                className="admin-input-focus"
                type="number"
                min="0"
                style={S.input}
                value={form.prize_pool}
                onChange={e => setForm({ ...form, prize_pool: e.target.value })}
                required
              />
            </div>
            <div>
              <label style={S.label}>Total Spots</label>
              <input
                className="admin-input-focus"
                type="number"
                min="2"
                style={S.input}
                value={form.total_spots}
                onChange={e => setForm({ ...form, total_spots: e.target.value })}
                required
              />
            </div>
          </div>

          {/* PRIZE DISTRIBUTION TIERS */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <label style={{ ...S.label, marginBottom: 0 }}>💰 Prize Distribution (Dream11 Style)</label>
              <button 
                type="button" 
                onClick={() => setForm({ ...form, prize_distribution: [...form.prize_distribution, { rank_from: "", rank_to: "", prize: "" }] })}
                style={{ ...S.miniBtn, background: COLORS.indigo + "20", color: COLORS.indigoLight, width: "auto", padding: "4px 10px", fontSize: "10px" }}
              >
                + Add Tier
              </button>
            </div>
            
            {form.prize_distribution.length === 0 ? (
              <div style={{ padding: "12px", border: `1px dashed ${COLORS.cardBorder}`, borderRadius: "10px", textAlign: "center", fontSize: "11px", color: COLORS.textDim }}>
                No tiers defined. Only total prize pool will be shown.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {form.prize_distribution.map((tier, idx) => (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr auto", gap: "8px", alignItems: "center" }}>
                    <input style={{ ...S.input, padding: "8px 12px" }} placeholder="Rank From" type="number" value={tier.rank_from} onChange={e => { const nd = [...form.prize_distribution]; nd[idx].rank_from = e.target.value; setForm({ ...form, prize_distribution: nd }); }} />
                    <input style={{ ...S.input, padding: "8px 12px" }} placeholder="Rank To" type="number" value={tier.rank_to} onChange={e => { const nd = [...form.prize_distribution]; nd[idx].rank_to = e.target.value; setForm({ ...form, prize_distribution: nd }); }} />
                    <input style={{ ...S.input, padding: "8px 12px" }} placeholder="Prize (₹)" type="number" value={tier.prize} onChange={e => { const nd = [...form.prize_distribution]; nd[idx].prize = e.target.value; setForm({ ...form, prize_distribution: nd }); }} />
                    <button type="button" onClick={() => setForm({ ...form, prize_distribution: form.prize_distribution.filter((_, i) => i !== idx) })} style={{ ...S.miniBtn, color: COLORS.red }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.btnRow}>
            {editingId && (
              <button type="button" style={S.btnCancel} onClick={() => { setEditingId(null); setForm({ title: "", entry_fee: "", prize_pool: "", total_spots: "", prize_distribution: [] }); }}>
                Cancel
              </button>
            )}
            <button type="submit" disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving..." : (editingId ? "Update Master Tier" : "Create Master Tier")}
            </button>
          </div>
        </form>
      </div>

      <div style={S.sectionTitle}>Master Tiers List ({templates.length})</div>
      {loading ? (
        <div style={S.loading}>Loading tiers...</div>
      ) : templates.length === 0 ? (
        <div style={S.empty}>No player tiers yet. Create one above.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "16px" }}>
          {templates.map(t => (
            <div key={t.id} className="admin-card-hover" style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "16px", padding: "20px", position: "relative" }}>
              <div style={{ position: "absolute", top: "12px", right: "12px", display: "flex", gap: "6px" }}>
                <button className="admin-btn-hover" style={S.miniBtn} onClick={() => handleEdit(t)}>✏️</button>
                <button className="admin-btn-hover" style={{ ...S.miniBtn, color: COLORS.red }} onClick={() => handleDelete(t.id, t.title)}>🗑️</button>
              </div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: COLORS.text, paddingRight: "60px", marginBottom: "14px" }}>
                🎴 {t.title}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div style={{ background: COLORS.inputBg, borderRadius: "10px", padding: "10px" }}>
                  <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase" }}>Entry Fee</div>
                  <div style={{ fontSize: "18px", fontWeight: 800, color: COLORS.text, marginTop: "4px" }}>₹{t.entry_fee}</div>
                </div>
                <div style={{ background: COLORS.inputBg, borderRadius: "10px", padding: "10px" }}>
                  <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, textTransform: "uppercase" }}>Prize Pool</div>
                  <div style={{ fontSize: "18px", fontWeight: 800, color: COLORS.green, marginTop: "4px" }}>₹{t.prize_pool}</div>
                </div>
              </div>
              
              {Array.isArray(t.prize_distribution) && t.prize_distribution.length > 0 && (
                <div style={{ marginTop: "14px", borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: "12px" }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: "8px" }}>Prize Breakup:</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {t.prize_distribution.map((tier, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                        <span style={{ color: COLORS.textMid }}>Rank {tier.rank_from}{tier.rank_to && tier.rank_to !== tier.rank_from ? `-${tier.rank_to}` : ""}:</span>
                        <span style={{ fontWeight: 700, color: COLORS.green }}>₹{tier.prize}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ManagePlayerTemplates = () => (
  <ToastProvider>
    <ManagePlayerTemplatesInner />
  </ToastProvider>
);

export default ManagePlayerTemplates;
